// ============================================================
// PERMISSION MATRIX CONFIG – Ma trận quyền role × action × module
// ============================================================

import type { UserRole } from './navigation.config';

export type Action =
  | 'view'
  | 'create'
  | 'update'
  | 'delete'
  | 'approve'
  | 'export'
  | 'invite_member'
  | 'remove_member'
  | 'assign_role'
  | 'manage_billing'
  | 'manage_security'
  | 'reject'
  | 'lock'
  | 'start'
  | 'complete'
  | 'close'
  | 'inspect'
  | 'assign'
  | 'dispatch'
  | 'check_in'
  | 'resolve'
  | 'mark_ready'
  | 'generate_schedule'
  | 'approve_payment'
  | 'record_payment'
  | 'create_payment_claim'
  | 'apply_process'
  | 'view_cost'
  | 'override_price';

export type Module =
  | 'projects'
  | 'boq'
  | 'progress'
  | 'inventory'
  | 'payments'
  | 'quality'
  | 'approvals'
  | 'maintenance'
  | 'assets'
  | 'maintenance_assets'
  | 'maintenance_plans'
  | 'maintenance_contracts'
  | 'maintenance_work_orders'
  | 'maintenance_logs'
  | 'maintenance_issues'
  | 'maintenance_workload'
  | 'maintenance_qa'
  | 'equipment_assets'
  | 'equipment'
  | 'utility'
  | 'zone_ops'
  | 'reports'
  | 'documents'
  | 'users'
  | 'roles'
  | 'companies'
  | 'audit_logs'
  | 'agro'
  | 'construction'
  | 'estimates'
  | 'settings';

type PermissionMatrix = Record<UserRole, Partial<Record<Module, Action[]>>>;

export const permissionMatrixConfig: PermissionMatrix = {
  super_admin: {
    projects: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    boq: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    progress: ['view', 'create', 'update', 'delete', 'export'],
    inventory: ['view', 'create', 'update', 'delete', 'export'],
    payments: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    quality: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    approvals: ['view', 'create', 'update', 'delete', 'approve'],
    maintenance: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    assets: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    maintenance_assets: ['view', 'create', 'update', 'delete'],
    maintenance_plans: ['view', 'create', 'update', 'delete', 'approve'],
    maintenance_contracts: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    maintenance_work_orders: ['view', 'create', 'update', 'delete', 'approve'],
    maintenance_logs: ['view', 'create', 'update', 'delete'],
    maintenance_issues: ['view', 'create', 'update', 'delete'],
    maintenance_qa: ['view', 'create', 'update', 'delete', 'approve'],
    maintenance_workload: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    equipment_assets: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    utility: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    zone_ops: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    reports: ['view', 'create', 'export'],
    documents: ['view', 'create', 'update', 'delete'],
    users: ['view', 'create', 'update', 'delete'],
    roles: ['view', 'create', 'update', 'delete'],
    companies: ['view', 'create', 'update', 'delete', 'export', 'invite_member', 'remove_member', 'assign_role', 'manage_billing', 'manage_security'],
    audit_logs: ['view', 'export'],
    agro: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    estimates: ['view', 'create', 'update', 'delete', 'approve', 'export', 'lock', 'apply_process', 'view_cost', 'override_price'],
    settings: ['view', 'update'],
  },
  admin: {
    projects: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    boq: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    progress: ['view', 'create', 'update', 'delete', 'export'],
    inventory: ['view', 'create', 'update', 'delete', 'export'],
    payments: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    quality: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    approvals: ['view', 'create', 'update', 'delete', 'approve'],
    maintenance: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    assets: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    maintenance_assets: ['view', 'create', 'update', 'delete'],
    maintenance_plans: ['view', 'create', 'update', 'delete', 'approve'],
    maintenance_contracts: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    maintenance_work_orders: ['view', 'create', 'update', 'delete', 'approve'],
    maintenance_logs: ['view', 'create', 'update', 'delete'],
    maintenance_issues: ['view', 'create', 'update', 'delete'],
    maintenance_qa: ['view', 'create', 'update', 'delete', 'approve'],
    maintenance_workload: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    equipment_assets: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    utility: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    zone_ops: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    reports: ['view', 'create', 'export'],
    documents: ['view', 'create', 'update', 'delete'],
    users: ['view', 'create', 'update', 'delete'],
    roles: ['view', 'create', 'update', 'delete'],
    companies: ['view', 'create', 'update', 'delete', 'export', 'invite_member', 'remove_member', 'assign_role', 'manage_billing', 'manage_security'],
    audit_logs: ['view', 'export'],
    estimates: ['view', 'create', 'update', 'delete', 'approve', 'export', 'lock', 'apply_process', 'view_cost', 'override_price'],
    settings: ['view', 'update'],
  },
  director: {
    projects: ['view', 'approve', 'export'],
    boq: ['view', 'approve', 'export'],
    progress: ['view', 'export'],
    inventory: ['view', 'export'],
    payments: ['view', 'approve', 'export'],
    quality: ['view', 'approve', 'export'],
    approvals: ['view', 'approve'],
    maintenance: ['view', 'export'],
    assets: ['view', 'export'],
    maintenance_assets: ['view'],
    maintenance_plans: ['view', 'approve'],
    maintenance_contracts: ['view', 'approve', 'export'],
    maintenance_work_orders: ['view'],
    maintenance_logs: ['view'],
    maintenance_issues: ['view'],
    maintenance_qa: ['view', 'approve', 'export'],
    maintenance_workload: ['view', 'approve', 'export'],
    equipment_assets: ['view', 'approve', 'export'],
    utility: ['view', 'approve', 'export'],
    zone_ops: ['view', 'approve', 'export'],
    reports: ['view', 'export'],
    documents: ['view'],
    companies: ['view', 'export'],
    estimates: ['view', 'approve', 'export'],
    settings: ['view'],
  },
  pm: {
    projects: ['view', 'create', 'update', 'export'],
    boq: ['view', 'create', 'update', 'export'],
    progress: ['view', 'create', 'update', 'export'],
    inventory: ['view', 'export'],
    payments: ['view', 'create', 'export'],
    quality: ['view', 'export'],
    approvals: ['view', 'create'],
    maintenance: ['view'],
    maintenance_assets: ['view'],
    maintenance_plans: ['view'],
    maintenance_contracts: ['view', 'create', 'update', 'export'],
    maintenance_work_orders: ['view'],
    maintenance_logs: ['view'],
    maintenance_issues: ['view'],
    maintenance_qa: ['view', 'export'],
    maintenance_workload: ['view', 'export'],
    equipment_assets: ['view', 'export'],
    utility: ['view', 'export'],
    zone_ops: ['view', 'export'],
    reports: ['view', 'create', 'export'],
    documents: ['view', 'create', 'update'],
    estimates: ['view', 'create', 'update', 'export', 'apply_process'],
    settings: ['view'],
  },
  qa_qc: {
    projects: ['view'],
    boq: ['view'],
    progress: ['view'],
    quality: ['view', 'create', 'update', 'export'],
    approvals: ['view', 'create'],
    maintenance: ['view'],
    maintenance_issues: ['view', 'create'],
    maintenance_qa: ['view', 'create', 'update', 'approve', 'export'],
    reports: ['view', 'export'],
    documents: ['view', 'create', 'update'],
    settings: ['view'],
  },
  site_manager: {
    projects: ['view', 'update'],
    boq: ['view'],
    progress: ['view', 'create', 'update', 'export'],
    inventory: ['view', 'create'],
    payments: ['view'],
    quality: ['view', 'create', 'update'],
    approvals: ['view', 'create'],
    maintenance: ['view', 'create', 'update', 'approve', 'export'],
    assets: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    maintenance_assets: ['view', 'create', 'update'],
    maintenance_plans: ['view', 'create', 'update'],
    maintenance_contracts: ['view', 'create', 'update', 'approve', 'export'],
    maintenance_work_orders: ['view', 'create', 'update', 'approve'],
    maintenance_logs: ['view', 'create', 'update'],
    maintenance_issues: ['view', 'create', 'update'],
    maintenance_qa: ['view', 'create', 'update', 'approve'],
    maintenance_workload: ['view', 'create', 'update', 'approve', 'export'],
    equipment_assets: ['view', 'create', 'update', 'approve', 'export'],
    utility: ['view', 'create', 'update', 'approve', 'export'],
    zone_ops: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    reports: ['view', 'create', 'export'],
    documents: ['view', 'create', 'update'],
    settings: ['view', 'update'],
  },
  warehouse: {
    projects: ['view'],
    inventory: ['view', 'create', 'update', 'export'],
    reports: ['view', 'export'],
    settings: ['view'],
  },
  accountant: {
    projects: ['view'],
    boq: ['view', 'create', 'update', 'export'],
    progress: ['view'],
    inventory: ['view', 'export'],
    payments: ['view', 'create', 'update', 'approve', 'export'],
    quality: ['view'],
    approvals: ['view', 'create'],
    maintenance: ['view'],
    maintenance_workload: ['view', 'export'],
    reports: ['view', 'create', 'export'],
    documents: ['view', 'create'],
    settings: ['view'],
  },
  supervisor: {
    projects: ['view'],
    maintenance: ['view', 'create', 'update', 'export'],
    assets: ['view', 'create', 'update', 'export'],
    maintenance_assets: ['view', 'create', 'update'],
    maintenance_plans: ['view', 'create', 'update', 'approve'],
    maintenance_work_orders: ['view', 'create', 'update', 'approve'],
    maintenance_logs: ['view', 'create', 'update'],
    maintenance_issues: ['view', 'create', 'update'],
    maintenance_workload: ['view', 'create', 'update', 'approve', 'export'],
    equipment_assets: ['view', 'create', 'update', 'export'],
    utility: ['view', 'create', 'update', 'approve', 'export'],
    zone_ops: ['view', 'create', 'update', 'approve', 'export'],
    reports: ['view', 'export'],
    settings: ['view'],
  },
  staff: {
    projects: ['view'],
    maintenance: ['view'],
    maintenance_assets: ['view'],
    maintenance_work_orders: ['view', 'update'],
    maintenance_logs: ['view', 'create'],
    maintenance_issues: ['view', 'create'],
    settings: ['view'],
  },
  site_foreman: {
    projects: ['view'],
    maintenance: ['view', 'create', 'update'],
    maintenance_assets: ['view', 'update'],
    maintenance_plans: ['view'],
    maintenance_work_orders: ['view', 'create', 'update', 'approve'],
    maintenance_logs: ['view', 'create', 'update'],
    maintenance_issues: ['view', 'create', 'update'],
    maintenance_qa: ['view', 'create'],
    zone_ops: ['view', 'update'],
    quality: ['view', 'create'],
    inventory: ['view', 'create'],
    reports: ['view', 'create'],
    documents: ['view', 'create'],
    settings: ['view', 'update'],
  },
  qs: {
    projects: ['view', 'export'],
    boq: ['view', 'create', 'update', 'export'],
    progress: ['view', 'export'],
    payments: ['view', 'create', 'update', 'export'],
    inventory: ['view', 'export'],
    approvals: ['view', 'create'],
    reports: ['view', 'create', 'export'],
    documents: ['view', 'create'],
    estimates: ['view', 'create', 'update', 'export', 'apply_process', 'view_cost', 'override_price'],
    settings: ['view'],
  },
  secretary: {
    projects: ['view'],
    boq: ['view'],
    progress: ['view'],
    quality: ['view'],
    approvals: ['view', 'create'],
    documents: ['view', 'create', 'update'],
    reports: ['view', 'export'],
    settings: ['view'],
  },
  equipment_manager: {
    projects: ['view'],
    maintenance: ['view', 'create', 'update', 'export'],
    maintenance_assets: ['view', 'create', 'update'],
    maintenance_plans: ['view', 'create', 'update'],
    maintenance_work_orders: ['view', 'create', 'update', 'approve'],
    maintenance_logs: ['view', 'create', 'update'],
    maintenance_issues: ['view', 'create', 'update'],
    equipment_assets: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    utility: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    inventory: ['view', 'export'],
    reports: ['view', 'export'],
    settings: ['view'],
  },
  zone_manager: {
    projects: ['view', 'update'],
    progress: ['view', 'create', 'update', 'export'],
    inventory: ['view', 'create'],
    quality: ['view', 'create', 'update'],
    maintenance: ['view', 'create', 'update'],
    maintenance_assets: ['view', 'create', 'update'],
    maintenance_work_orders: ['view', 'create', 'update'],
    maintenance_issues: ['view', 'create', 'update'],
    maintenance_logs: ['view', 'create'],
    maintenance_qa: ['view', 'create'],
    equipment_assets: ['view', 'update', 'export'],
    zone_ops: ['view', 'create', 'update', 'delete', 'approve', 'export'],
    reports: ['view', 'export'],
    documents: ['view', 'create'],
    settings: ['view'],
  },
  agro_manager: {
    projects: ['view'],
    inventory: ['view', 'create', 'update', 'export'],
    maintenance: ['view', 'create', 'update'],
    assets: ['view', 'create', 'update', 'export'],
    maintenance_assets: ['view', 'create', 'update'],
    maintenance_work_orders: ['view', 'create', 'update'],
    maintenance_issues: ['view', 'create', 'update'],
    maintenance_logs: ['view', 'create'],
    zone_ops: ['view', 'export'],
    reports: ['view', 'export'],
    documents: ['view', 'create'],
    settings: ['view'],
  },
};

/**
 * Kiểm tra quyền của user
 */
export function hasPermission(
  role: UserRole,
  module: Module,
  action: Action
): boolean {
  const rolePerms = permissionMatrixConfig[role];
  if (!rolePerms) return false;
  const modulePerms = rolePerms[module];
  if (!modulePerms) return false;
  return modulePerms.includes(action);
}
